import { useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../styled-system/css";
import DOMPurify from "dompurify";
import { Tabs, TabList, Tab, TabPanel } from "../../components/core/basic/Tabs";

type Note = {
  note_id: string;
  body: string;
};

type Task = {
  task_id: string;
  subject: string;
  body: string;
};

type Reviews = {
  response: string;
  messages: string;
};

interface NoteCardProps {
  content: string;
  notes: Note[];
  tasks: Task[];
  reviews: Reviews[];
}

type tab = "content" | "notes" | "tasks" | "reviews";

function NoteCard({ content, notes, tasks, reviews }: NoteCardProps) {
  const { t } = useTranslation(["hubspot"]);
  const [selectedTab, setSelectedTab] = useState<tab>("content");

  return (
    <Tabs selectedKey={selectedTab} onSelectionChange={(key) => setSelectedTab(key as tab)}>
      <TabList>
        <Tab id="content">{t(($) => $.hubspot["Content"])}</Tab>
        <Tab id="notes">{t(($) => $.hubspot["Notes"])}</Tab>
        <Tab id="tasks">{t(($) => $.hubspot["Tasks"])}</Tab>
        <Tab id="reviews">{t(($) => $.hubspot["Weekly Review"])}</Tab>
      </TabList>
      <TabPanel id="content">
        <fieldset>
          <legend className={css({ color: "trellisPalette.13", fontWeight: "bold" })}>
            {t(($) => $.hubspot["Description"])}
          </legend>
          <p>{content}</p>
        </fieldset>
      </TabPanel>
      <TabPanel id="notes">
        {notes.length === 0 ?
          <p> {t(($) => $.hubspot["No Notes Available"])}</p>
        : <fieldset>
            <legend className={css({ color: "trellisPalette.13", fontWeight: "bold" })}>
              {t(($) => $.hubspot["Notes"])}
            </legend>
            <ol className={css({ "& li::marker": { color: "#999", fontSize: "16px" } })}>
              {notes.map((note) => (
                <li key={note.note_id}>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.body) }} />
                </li>
              ))}
            </ol>
          </fieldset>
        }
      </TabPanel>
      <TabPanel id="tasks">
        {tasks.length === 0 ?
          <p> {t(($) => $.hubspot["No Tasks Available"])}</p>
        : <fieldset>
            <legend className={css({ color: "trellisPalette.13", fontWeight: "bold" })}>
              {t(($) => $.hubspot["Tasks"])}
            </legend>
            <ol className={css({ "& li::marker": { color: "#999", fontSize: "16px" } })}>
              {tasks.map((task) => (
                <li key={task.task_id}>
                  <p>
                    <span className={css({ color: "trellisPalette.13", fontSize: "large", fontWeight: "medium" })}>
                      {t(($) => $.hubspot["Subject:"])}{" "}
                    </span>
                    {task.subject}
                  </p>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(task.body) }} />
                </li>
              ))}
            </ol>
          </fieldset>
        }
      </TabPanel>
      <TabPanel id="reviews">
        {reviews.length === 0 ?
          <p>{t(($) => $.hubspot["Not Available"])}</p>
        : <div>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reviews[0]?.response || "") }} />
          </div>
        }
      </TabPanel>
    </Tabs>
  );
}

export default NoteCard;
